'use client';

import React, { useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import AOS from 'aos';
import 'aos/dist/aos.css';

import styles from './CollageGallery.module.scss';
import { Container } from '@mantine/core';

export interface CollageItem {
    id: string;
    type: 'image' | 'text';
    src?: string;
    alt?: string;
    text?: string;
    imageTitle?: string;
    size?: 'portrait' | 'landscape' | 'square';
}

interface CollageGalleryProps {
    title?: string;
    items: CollageItem[];
    backgroundImageUrl?: string;
    ctaLabel?: string;
    ctaUrl?: string;
}

const CollageGallery: React.FC<CollageGalleryProps> = ({
    title,
    items,
    backgroundImageUrl,
    ctaLabel,
    ctaUrl,
}) => {
    useEffect(() => {
        AOS.init({
            once: true,
            duration: 900,
            easing: 'ease-out-cubic',
            offset: 120,
        });
    }, []);

    return (
        <Container size="xl"
            className={styles.galleryWrapper}
            style={
                backgroundImageUrl
                    ? ({
                        ['--bg-image' as any]: `url(${backgroundImageUrl})`,
                    } as React.CSSProperties)
                    : undefined
            }
            data-aos="fade"
        >
            <header className={styles.header} data-aos="fade-up">
                {title && <h2 className={styles.title}>{title}</h2>}
            </header>

            <div className={styles.squareWrapper}>
                <div className={styles.grid}>
                    {items.map((item, idx) => {
                        const sizeClass =
                            item.size === 'portrait'
                                ? styles.portrait
                                : item.size === 'landscape'
                                    ? styles.landscape
                                    : styles.square;

                        const animation =
                            item.type === 'text' ? 'fade-up' : 'zoom-in';

                        return (
                            <article
                                key={item.id}
                                className={`${styles.card} ${sizeClass}`}
                                data-aos={animation}
                                data-aos-delay={idx * 200}
                            >
                                {item.type === 'image' && item.src ? (
                                    <figure className={styles.imageCard}>
                                        <Image
                                            src={item.src}
                                            alt={item.alt || item.imageTitle || 'Artwork'}
                                            fill
                                            sizes="(max-width: 600px) 100vw, (max-width: 1024px) 90vw, 1100px"
                                            className={styles.image}
                                            priority={idx === 0}
                                        />
                                        {item.imageTitle && (
                                            <figcaption className={styles.label}>
                                                {item.imageTitle}
                                            </figcaption>
                                        )}
                                    </figure>
                                ) : (
                                    <div className={styles.textCard}>
                                        <p className={styles.text}>{item.text}</p>
                                    </div>
                                )}
                            </article>
                        );
                    })}
                </div>
            </div>

            {ctaLabel && ctaUrl && (
                <div className={styles.ctaWrapper} data-aos="fade-up" data-aos-delay="200">
                    <Link href={ctaUrl} className={styles.ctaBtn}>
                        {ctaLabel}
                    </Link>
                </div>
            )}
        </Container>
    );
};

export default CollageGallery;
